import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../components/Icon';
import KpiCard from '../components/KpiCard';
import { TrendChart } from '../components/DashboardCharts';
import {
  DashboardSummary,
  DashboardAnalytics,
  getDashboardSummary,
  getDashboardAnalytics,
} from '../api/dashboard';
import { extractApiError } from '../api/legalServices';

const money = (n: number): string => '$' + Math.round(n).toLocaleString();
const count = (n: number): string => n.toLocaleString();

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // Analytics load independently so a graph failure never blocks the dashboard.
  const [analytics, setAnalytics] = useState<DashboardAnalytics | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getDashboardSummary();
      setData(res);
    } catch (err) {
      setError(extractApiError(err, 'Failed to load dashboard'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    getDashboardAnalytics()
      .then((a) => {
        if (!cancelled) setAnalytics(a);
      })
      .catch(() => {
        if (!cancelled) setAnalytics(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="py-20 text-center text-g500">
        <Icon name="layout-dashboard" size={28} color="var(--g400)" /> Loading dashboard...
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="card p-6 max-w-[520px]">
        <div className="flex items-center gap-2 mb-2" style={{ color: 'var(--red)' }}>
          <Icon name="x-circle" size={18} color="var(--red)" />
          <strong>Couldn't load dashboard</strong>
        </div>
        <div className="text-sm text-g600 mb-4">{error || 'No data returned.'}</div>
        <button className="btn btn-secondary btn-sm" onClick={load}>
          Try again
        </button>
      </div>
    );
  }

  const { kpis, quickActions, revenueBreakdown, activity } = data;

  /*
   * The platform earns from paid service orders (recorded as 'Commission'),
   * rental application fees ('App Fee') and subscriptions. 'Listing Fee' is
   * never charged, so it is dropped. Labels match the Revenue page:
   * "Legal Services Request" and "Rental Application".
   */
  const displayBreakdown = revenueBreakdown
    .filter((r) => r.label !== 'Listing Fee')
    .map((r) => ({
      ...r,
      label:
        r.label === 'Commission'
          ? 'Legal Services Request'
          : r.label === 'App Fee'
          ? 'Rental Application'
          : r.label,
    }));

  const quickActionRows = [
    { l: 'Review Pending Listings', c: quickActions.pendingListings, i: 'building-2',     p: '/admin/listings' },
    { l: 'Respond to Inquiries',    c: quickActions.inquiries,       i: 'message-square', p: '/admin/leads' },
    { l: 'Approve New Merchants',      c: quickActions.newAgents,       i: 'user-check',     p: '/admin/agents' },
    { l: 'Open Legal Services Requests', c: quickActions.serviceOrders, i: 'package',     p: '/admin/orders' },
  ];

  return (
    <div>
      {/* KPI row */}
      <div className="flex gap-4 flex-wrap mb-6">
        <KpiCard
          icon="building-2"
          label="Total Listings"
          value={kpis.totalListings.valueLabel ?? String(kpis.totalListings.value)}
          change={kpis.totalListings.change}
          up={kpis.totalListings.up}
          delay={0}
          onClick={() => navigate('/admin/listings')}
        />
        <KpiCard
          icon="user-check"
          label="Active Merchants"
          value={kpis.activeAgents.valueLabel ?? String(kpis.activeAgents.value)}
          change={kpis.activeAgents.change}
          up={kpis.activeAgents.up}
          delay={80}
          onClick={() => navigate('/admin/agents')}
        />
        <KpiCard
          icon="message-square"
          label="Pending Inquiries"
          value={kpis.pendingInquiries.valueLabel ?? String(kpis.pendingInquiries.value)}
          change={kpis.pendingInquiries.change}
          up={kpis.pendingInquiries.up}
          delay={160}
          onClick={() => navigate('/admin/leads')}
        />
        <KpiCard
          icon="dollar-sign"
          label="Revenue This Month"
          value={kpis.monthRevenue.valueLabel ?? String(kpis.monthRevenue.value)}
          change={kpis.monthRevenue.change}
          up={kpis.monthRevenue.up}
          delay={240}
          onClick={() => navigate('/admin/revenue')}
        />
      </div>

      {/* Analytics — 30-day trends */}
      {analytics && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-lg font-bold">Analytics</span>
            <span className="text-[12px] text-g400">last 30 days</span>
          </div>
          <div
            className="grid gap-4"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))' }}
          >
            <TrendChart title="User Registrations" points={analytics.series.registrations} color="var(--brand)" format={count} />
            <TrendChart title="Revenue" points={analytics.series.revenue} color="var(--green)" format={money} />
            <TrendChart title="New Listings" points={analytics.series.listings} color="var(--blue)" format={count} />
            <TrendChart title="New Inquiries" points={analytics.series.inquiries} color="var(--brand)" format={count} />
            <TrendChart title="Rental Applications" points={analytics.series.applications} color="var(--green)" format={count} />
            <TrendChart title="Legal Orders" points={analytics.series.orders} color="var(--blue)" format={count} />
          </div>
        </div>
      )}

      {/* Main + side */}
      <div className="flex gap-5 flex-wrap">
        {/* Recent Activity */}
        <div className="flex-[2] min-w-[400px]">
          <div className="card animate-fadeIn" style={{ animationDelay: '300ms' }}>
            <div className="flex justify-between items-center px-6 py-5">
              <span className="text-base font-bold">Recent Activity</span>
            </div>
            <div className="px-6 pb-5">
              {activity.length === 0 ? (
                <div className="py-10 text-center text-sm text-g500">
                  No recent activity yet.
                </div>
              ) : (
                activity.map((a, idx) => (
                  <div
                    key={idx}
                    onClick={() => navigate('/admin/' + a.link)}
                    className={
                      'flex items-start gap-3.5 py-3.5 cursor-pointer ' +
                      (idx !== activity.length - 1 ? 'border-b border-g100' : '')
                    }
                  >
                    <div
                      className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0"
                      style={{
                        background:
                          a.color === 'green'
                            ? 'rgba(5,150,105,.08)'
                            : 'var(--brand-bg)',
                      }}
                    >
                      <Icon
                        name={a.icon}
                        size={18}
                        color={a.color === 'green' ? 'var(--green)' : 'var(--brand)'}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-ink">{a.text}</div>
                      <div className="text-[13px] text-g500 mt-0.5">{a.detail}</div>
                    </div>
                    <span className="text-xs text-g400 whitespace-nowrap flex-shrink-0">
                      {a.time}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions + Revenue Breakdown */}
        <div className="flex-1 min-w-[280px]">
          <div
            className="card p-6 animate-fadeIn"
            style={{ animationDelay: '350ms' }}
          >
            <span className="text-base font-bold">Quick Actions</span>
            <div className="flex flex-col gap-2.5 mt-5">
              {quickActionRows.map((q) => (
                <div
                  key={q.l}
                  onClick={() => navigate(q.p)}
                  className="flex items-center gap-3 px-3.5 py-3 rounded-xl border border-g100 cursor-pointer transition-all hover:border-brand"
                  style={{ background: '#fff' }}
                >
                  <Icon name={q.i} size={18} />
                  <span className="flex-1 text-sm font-medium">{q.l}</span>
                  <span
                    className="text-xs font-bold text-brand px-2.5 py-0.5 rounded-full"
                    style={{ background: 'var(--brand-bg)' }}
                  >
                    {q.c}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 rounded-[14px] bg-g50">
              <div className="text-[13px] font-semibold text-g500 mb-3">
                Revenue Breakdown
              </div>
              {displayBreakdown.every((r) => r.amount === 0) ? (
                <div className="text-[13px] text-g400">No revenue this month yet.</div>
              ) : (
                displayBreakdown.map((r) => (
                  <div key={r.label} className="mb-2.5 last:mb-0">
                    <div className="flex justify-between text-[13px] mb-1">
                      <span className="font-medium">{r.label}</span>
                      <span className="font-semibold">{r.amountLabel}</span>
                    </div>
                    <div className="h-1.5 rounded" style={{ background: 'var(--g200)' }}>
                      <div
                        className="h-full rounded"
                        style={{
                          width: `${r.pct}%`,
                          background: 'var(--brand)',
                          opacity: 0.6 + (r.pct / 100) * 0.4,
                        }}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Dashboard;
